import React, { useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { Copy, Share2, Calendar } from 'lucide-react';
import { OtcHelper } from '../../utils/helpers/otc.helper';

const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

const getSeconds = timestamp => timestamp?._seconds ?? timestamp?.seconds ?? null;

const DisplayOtc = ({ otc, children, compact = false }) => {
    const [copied, setCopied] = useState(false);

    const startsAtSeconds = getSeconds(otc.starts_at);
    const expiresAtSeconds = getSeconds(otc.expires_at);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(otc.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard access denied or unavailable — button stays usable, just no "Copied!" confirmation.
        }
    };

    const handleShare = async () => {
        try {
            await navigator.share({
                title: 'SAGE One-Time Access Code',
                text: `SAGE One-Time Access Code${otc.name ? ` — ${otc.name}` : ''}: ${
                    otc.code
                }${
                    startsAtSeconds
                        ? `\nActivation: ${OtcHelper.formatTimestamp(startsAtSeconds)}`
                        : ''
                }${
                    expiresAtSeconds
                        ? `\nExpires: ${OtcHelper.formatTimestamp(expiresAtSeconds)}`
                        : ''
                }`
            });
        } catch (error) {
            // Ignore cancellations (AbortError) and other share failures silently
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                mx: 'auto',
                mb: compact ? 0 : 2,
                pt: compact ? 0 : 2,
                width: '100%',
            }}
        >
            {!compact && (
                <Typography
                    variant="body1"
                    color="secondary"
                    sx={{ fontSize: '16px', mb: 2.5, textAlign: 'center' }}
                >
                    Share this code with{' '}
                    <Typography
                        component="strong"
                        variant="body1"
                        sx={{
                            fontSize: '16px',
                            fontWeight: 700,
                            textTransform: 'capitalize'
                        }}
                    >
                        {otc.name}
                    </Typography>
                    .{' '}
                    {!otc.reusable
                        ? 'This code can only be used once.'
                        : 'This code can be used multiple times.'}
                </Typography>
            )}

            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'success.lighter',
                    border: '1px solid',
                    borderColor: 'success.dark',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    mb: compact ? 0 : 2.5,
                    gap: 2,
                    flexWrap: 'wrap'
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: 24, sm: 28 },
                        fontWeight: 700,
                        letterSpacing: 3,
                        color: 'success.dark'
                    }}
                >
                    {otc.code}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {canNativeShare && (
                        <Tooltip title="Share code" placement="top">
                            <Button
                                onClick={handleShare}
                                startIcon={<Share2 size={16} />}
                                sx={{
                                    color: 'success.dark',
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Share
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={copied ? 'Copied!' : 'Copy code'} placement="top">
                        <Button
                            onClick={handleCopy}
                            startIcon={<Copy size={16} />}
                            sx={{
                                color: 'success.dark',
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {!compact && (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mb: children ? 3 : 0
                    }}
                >
                    {startsAtSeconds && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: 'center'
                            }}
                        >
                            <Calendar size={14} />
                            <Typography variant="body2" color="secondary">
                                Activation:{' '}
                                <strong>{OtcHelper.formatTimestamp(startsAtSeconds)}</strong>
                            </Typography>
                        </Box>
                    )}
                    {expiresAtSeconds && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: 'center'
                            }}
                        >
                            <Calendar size={14} />
                            <Typography variant="body2" color="secondary">
                                Expires:{' '}
                                <strong>{OtcHelper.formatTimestamp(expiresAtSeconds)}</strong>
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {children}
        </Box>
    );
};

export default DisplayOtc;
